import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  DocumentMagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const entryPoints = [
  {
    id: 'career_coach',
    title: 'Interview Me',
    description: "Answer a few questions. I'll build your resume as you go. Best for most people.",
    icon: ChatBubbleLeftRightIcon,
    time: '~12 minutes',
    recommended: true,
    features: [
      'Guided questions extract your achievements',
      'AI polishes your answers into resume bullets',
      'Real-time preview as you answer'
    ]
  },
  {
    id: 'paste_transform',
    title: 'Paste My Experience',
    description: "Already have text? Paste your LinkedIn summary, old resume, or notes. I'll structure it.",
    icon: DocumentTextIcon,
    time: '~8 minutes',
    recommended: false,
    features: [
      'Paste any text - I\'ll extract the structure',
      'Review and correct what I found',
      'Polish weak bullets into strong ones'
    ]
  },
  {
    id: 'jd_match',
    title: 'Match a Job Posting',
    description: "Have a specific job in mind? Paste the job description and I'll help you tailor your resume.",
    icon: DocumentMagnifyingGlassIcon,
    time: '~15 minutes',
    recommended: false,
    features: [
      'Analyze job requirements',
      'Identify keyword gaps',
      'Build a tailored resume'
    ]
  }
];

const EntryPointSelector = ({ onSelect, loading }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [interviewMode, setInterviewMode] = useState('quick');

  const handleSelect = (entryPoint) => {
    setSelectedId(entryPoint.id);
  };

  const handleStart = () => {
    if (!selectedId) return;
    onSelect(selectedId, { interviewMode });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Let's build your resume
        </h1>
        <p className="text-lg text-gray-600">
          Choose how you'd like to start
        </p>
      </div>

      <div className="space-y-4">
        {entryPoints.map((entry) => {
          const Icon = entry.icon;
          const isSelected = selectedId === entry.id;

          return (
            <button
              key={entry.id}
              onClick={() => handleSelect(entry)}
              disabled={loading}
              className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 ${
                isSelected
                  ? 'border-primary-600 bg-primary-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                  isSelected ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <Icon className="w-6 h-6" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {entry.title}
                    </h3>
                    {entry.recommended && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Recommended
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 mb-3">
                    {entry.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-4 h-4" />
                      {entry.time}
                    </span>
                  </div>

                  {isSelected && (
                    <div className="mt-4 pt-4 border-t border-primary-200">
                      <ul className="space-y-2">
                        {entry.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {entry.id === 'career_coach' && (
                        <div className="mt-4 p-3 bg-white rounded-lg border border-primary-200">
                          <p className="text-sm font-medium text-gray-700 mb-2">Interview depth:</p>
                          <div className="flex gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="interviewMode"
                                value="quick"
                                checked={interviewMode === 'quick'}
                                onChange={(e) => setInterviewMode(e.target.value)}
                                className="text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm">Quick (8 questions)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="interviewMode"
                                value="deep"
                                checked={interviewMode === 'deep'}
                                onChange={(e) => setInterviewMode(e.target.value)}
                                className="text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm">Deep (15 questions)</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {isSelected && (
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="w-6 h-6 text-primary-600" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selectedId && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleStart}
            disabled={loading}
            className="px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting...
              </>
            ) : (
              <>
                Start Building
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

EntryPointSelector.propTypes = {
  onSelect: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default EntryPointSelector;
